import { Trash01, Plus, Copy04, Container } from '@untitled-ui/icons-react/build/cjs';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { buildStopOptions } from 'src/app/MyQuote/AddQuote/multiStop.helpers';
import VisualizeUnit from 'src/app/MyQuote/Modals/VisualizeUnit';
import ButtonCmp from 'src/components/ButtonCmp';
import InputText from 'src/components/InputText/InputText';
import SelectBox from 'src/components/SelectBox/SelectBox';
import Toggle from 'src/components/Toggle/Toggle';
import TooltipCmp from 'src/components/TooltipCmp';
import { selectFormData, selectIsHiddenFieldInBooking } from 'src/redux/bookingStepper.slice';
import { RootState } from 'src/redux/store';
import { calculateLinearFootage, formatLinearFootage, getCalculatedFreightClass, getLabelByValue, getStackingBlockedMessage, isStackingBlocked } from 'src/utils/CommonFunctions';

import { IDimension } from './Dimension.interface';

const MAX_LINE_ITEMS = 15;

export const initDimensionData: IDimension = {
    id: null,
    handlingUnit: undefined,
    handlingUnitNo: 0,
    weight: 0,
    width: 0,
    height: 0,
    totalWeight: 0,
    isStack: false,
    weightMeasurement: 'LBS',
    lengthMeasurement: 'IN',
    description: undefined,
    commodity: undefined,
    freightClass: 0,
    itemClassForDayross: 0,
    length: 0,
};

const LineItem = () => {
    const { t } = useTranslation();
    const shouldAnimateRef = useRef(true);
    const isHiddenFieldInBooking = useSelector(selectIsHiddenFieldInBooking);
    const { handlingUnitOptions, commodityOptions } = useSelector((state: RootState) => state.basic);
    const orderDetails = useSelector(selectFormData);
    const { control, watch, setValue, trigger, formState, getValues, clearErrors } = useFormContext();
    const { isSubmitted } = formState;
    const [currentData, setCurrentData] = useState<{
        index: number;
        data: any;
    } | null>(null);

    const {
        fields: dimensionsOptionFields,
        append: appendOption,
        remove: removeOption,
    } = useFieldArray({
        control,
        name: 'dimensions',
        keyName: 'uuid',
    });

    const weightMeasurement = watch('weightMeasurement');
    const lengthMeasurement = watch('lengthMeasurement');
    const lengthMeasurementLabel = getLabelByValue(watch('lengthMeasurement'));
    const primaryEquipmentType = useMemo(() => orderDetails?.orderDetails?.equipmentType, [orderDetails?.orderDetails?.equipmentType]);

    // Client decision — read-only "Picks up at"/"Delivers at" per line item, matching the Add
    // Quote wizard's own per-item routing display exactly (same buildStopOptions helper, same
    // label format). orderDetails.stops (Redux) holds only the MIDDLE stops (see QuoteDetail/
    // index.tsx's handleBookOrder) — reshaped here into buildStopOptions' expected
    // Record<position, stop> shape. Disabled — this step never edits routing, only displays it.
    const middleStops: any[] = orderDetails?.stops || [];
    const stopCount = 2 + middleStops.length;
    const stopsByPosition = useMemo(
        () =>
            middleStops.reduce((acc: Record<number, any>, stop: any) => {
                acc[stop.position] = { companyName: stop.companyName, stopAddress: { city: stop.city, state: stop.state, stateCode: stop.stateCode }, activityHint: stop.activityHint };

                return acc;
            }, {}),
        [middleStops]
    );
    const { pickupOptions, dropOptions } = useMemo(
        () =>
            buildStopOptions(t, {
                stopCount,
                shipperCity: orderDetails?.shipperDetails?.shipperCity,
                shipperState: orderDetails?.shipperDetails?.shipperState,
                shipperStateCode: orderDetails?.shipperDetails?.shipperStateCode,
                shipperCompanyName: orderDetails?.shipperDetails?.shipperCompanyName,
                consigneeCity: orderDetails?.consigneeDetails?.consigneeCity,
                consigneeState: orderDetails?.consigneeDetails?.consigneeState,
                consigneeStateCode: orderDetails?.consigneeDetails?.consigneeStateCode,
                consigneeCompanyName: orderDetails?.consigneeDetails?.consigneeCompanyName,
                stops: stopsByPosition,
            }),
        [
            t,
            stopCount,
            orderDetails?.shipperDetails?.shipperCity,
            orderDetails?.shipperDetails?.shipperState,
            orderDetails?.shipperDetails?.shipperStateCode,
            orderDetails?.shipperDetails?.shipperCompanyName,
            orderDetails?.consigneeDetails?.consigneeCity,
            orderDetails?.consigneeDetails?.consigneeState,
            orderDetails?.consigneeDetails?.consigneeStateCode,
            orderDetails?.consigneeDetails?.consigneeCompanyName,
            stopsByPosition,
        ]
    );
    const dimensions = watch('dimensions');
    useEffect(() => {
        setTimeout(() => {
            shouldAnimateRef.current = false;
        }, 200);
    }, []);

    useEffect(() => {
        const calLinearFootage = formatLinearFootage(calculateLinearFootage(dimensions, primaryEquipmentType, true, weightMeasurement)) || 0;
        setValue('linearFootage', calLinearFootage);

        if (isSubmitted) {
            trigger('linearFootage');
        }
    }, [
        primaryEquipmentType,
        weightMeasurement,
        JSON.stringify(
            dimensions?.map((item: any) => ({
                handlingUnitNo: item.handlingUnitNo,
                width: item.width,
                height: item.height,
                length: item.length,
                weight: item.weight,
            }))
        ),
    ]);

    // Force isStack=true on any row whose height × 2 exceeds the bucket's stacking ceiling. Re-fires on measurement change because cm vs in changes the effective inches value.
    useEffect(() => {
        dimensions?.forEach((dim: any, idx: number) => {
            if (isStackingBlocked(primaryEquipmentType, dim?.height, weightMeasurement) && !dim?.isStack) {
                setValue(`dimensions.${idx}.isStack`, true);
            }
        });
    }, [primaryEquipmentType, weightMeasurement, JSON.stringify(dimensions?.map((d: any) => ({ h: d?.height, s: d?.isStack })))]);

    const handleOption = (onChange: any, name: string) => (e: any) => {
        onChange(e?.value);
        clearErrors(name);
    };

    const handleRemove = (index: number) => (e: any) => {
        e.stopPropagation();

        if (dimensionsOptionFields.length > 1) {
            removeOption(index);

            // Trigger validation after removing if form was already submitted
            if (isSubmitted) {
                // Use setTimeout to trigger after the removal has been processed
                setTimeout(() => {
                    trigger('dimensions');
                }, 0);
            }
        } else {
            // Reset to initial dimension data
            clearErrors('dimensions');
            setValue('dimensions', [initDimensionData]);
        }
    };

    const handleAdd = (e: any) => {
        e.stopPropagation();
        shouldAnimateRef.current = false;

        if (dimensionsOptionFields.length >= MAX_LINE_ITEMS) {
            return;
        }

        const newOption: IDimension = initDimensionData;
        appendOption(newOption);
    };

    const handleDuplicate = (index: number) => (e: any) => {
        e.stopPropagation();
        shouldAnimateRef.current = false;

        if (dimensionsOptionFields.length >= MAX_LINE_ITEMS) {
            return;
        }

        const fieldValues = watch(`dimensions.${index}`);
        appendOption({ ...fieldValues, id: null });
    };

    const handleInputChange = (index: number) => (event: any) => {
        const lineItemData = watch(`dimensions.${index}`);
        const { target, type: eventType } = event;
        let { name, value, type } = target;

        // Clear error for this field when user starts typing
        clearErrors(`dimensions.${index}.${name}`);

        // Clear errors for both weight and totalWeight when either changes
        if (name === 'weight' || name === 'totalWeight') {
            clearErrors(`dimensions.${index}.weight`);
            clearErrors(`dimensions.${index}.totalWeight`);
        }

        if (type === 'number') {
            value = value ? parseFloat(value) : eventType === 'blur' ? 0 : value;
        }

        let totalWeight = lineItemData.totalWeight || 0;
        let weight = lineItemData.weight || 0;
        let handlingUnitNo = lineItemData.handlingUnitNo || 0;

        // If handlingUnitNo or weight changes → update totalWeight = handlingUnitNo * weight
        if (name === 'handlingUnitNo') {
            handlingUnitNo = value;

            if (handlingUnitNo > 0 && weight > 0) {
                totalWeight = handlingUnitNo * weight;

                if (Number.isInteger(totalWeight)) {
                    totalWeight = parseInt(totalWeight);
                } else {
                    totalWeight = Math.floor(totalWeight * 100) / 100;
                }
            } else {
                totalWeight = 0;
            }
        } else if (name === 'weight') {
            weight = value;

            if (handlingUnitNo > 0 && weight > 0) {
                totalWeight = handlingUnitNo * weight;

                if (Number.isInteger(totalWeight)) {
                    totalWeight = parseInt(totalWeight);
                } else {
                    totalWeight = Math.floor(totalWeight * 100) / 100;
                }
            } else {
                totalWeight = 0;
            }
        } else if (name === 'totalWeight') {
            // If totalWeight changes → update weight = totalWeight / handlingUnitNo
            totalWeight = typeof value === 'number' ? value : parseFloat(value) || 0;

            if (handlingUnitNo > 0 && totalWeight > 0) {
                weight = totalWeight / handlingUnitNo;

                if (Number.isInteger(weight)) {
                    weight = parseInt(weight);
                } else {
                    weight = Math.floor(weight * 100) / 100;
                }
            } else {
                weight = 0;
            }
        }

        // Handle legacy 'weight' field for backward compatibility
        if (name === 'weight') {
            weight = value;

            if (handlingUnitNo > 0 && weight > 0) {
                totalWeight = handlingUnitNo * weight;

                if (Number.isInteger(totalWeight)) {
                    totalWeight = parseInt(totalWeight);
                } else {
                    totalWeight = Math.floor(totalWeight * 100) / 100;
                }
            } else {
                totalWeight = 0;
            }
        }

        if (isNaN(totalWeight) || !isFinite(totalWeight)) {
            totalWeight = 0;
        }

        if (isNaN(weight) || !isFinite(weight)) {
            weight = 0;
        }

        // Ensure totalWeight is a number before calling toFixed
        const totalWeightNumber = typeof totalWeight === 'number' ? totalWeight : parseFloat(totalWeight) || 0;

        setValue(`dimensions.${index}.${name}`, value);
        setValue(`dimensions.${index}.totalWeight`, parseFloat(totalWeightNumber.toFixed(2)));
        setValue(`dimensions.${index}.weight`, typeof weight === 'number' && !isNaN(weight) ? parseFloat(weight.toFixed(2)) : weight || 0);
        // Also update weight field for backward compatibility
        setValue(`dimensions.${index}.weight`, typeof weight === 'number' && !isNaN(weight) ? parseFloat(weight.toFixed(2)) : weight || 0);

        if (isSubmitted) {
            trigger(`dimensions.${index}.${name}`);
            trigger(`dimensions.${index}.totalWeight`);
            trigger(`dimensions.${index}.weight`);
        }
    };

    const handleChangeWithErrorClear = (index: number, fieldName: string) => (onChange: any) => (e: any) => {
        // Clear error for this field when user starts typing
        clearErrors(`dimensions.${index}.${fieldName}`);
        // Handle SelectBox case where value is in e.value
        onChange(e?.value !== undefined ? e.value : e);

        if (isSubmitted) {
            trigger(`dimensions.${index}.${fieldName}`);
        }
    };

    const checkFreightClass = (index: number) => (event: any) => {
        const lineItemData = watch(`dimensions.${index}`);
        const { target, type: eventType } = event;
        let { name, value, type } = target;

        // Clear error for this field when user starts typing
        clearErrors(`dimensions.${index}.${name}`);

        value = type === 'number' ? (value ? parseFloat(value) : eventType === 'blur' ? 0 : value) : value;
        let freightClass = lineItemData.freightClass;
        let itemClassForDayross = lineItemData.itemClassForDayross;
        let weight = lineItemData.weight;
        let width = lineItemData.width;
        let height = lineItemData.height;
        let length = lineItemData.length;

        if (weight && width && length && height) {
            const freightData = getCalculatedFreightClass({
                itemLength: length || 0,
                width: width || 0,
                height: height || 0,
                weight: weight || 0,
                lengthUnit: lengthMeasurement,
                weightUnit: weightMeasurement,
            });

            freightClass = freightData?.freightClass;
            itemClassForDayross = freightData?.itemClassForDayross;
        } else {
            freightClass = 0;
        }
        setValue(`dimensions.${index}.${name}`, value);
        setValue(`dimensions.${index}.freightClass`, freightClass);
        setValue(`dimensions.${index}.itemClassForDayross`, itemClassForDayross);

        if (freightClass) {
            clearErrors(`dimensions.${index}.freightClass`);
        }

        if (isSubmitted) {
            trigger(`dimensions.${index}.${name}`);
        }
    };

    const handleVisualizeUnit = (index: number) => (e: any) => {
        e.stopPropagation();
        setCurrentData({
            ...{ index: index, data: getValues(`dimensions.${index}`) },
        });
    };

    const handleVisualizeUnitClose = () => {
        setCurrentData(null);
    };

    const handleVisualizeUnitSubmit = (index: number, data: any) => {
        const fieldArray = ['itemLength', 'width', 'height', 'handlingUnit'];
        fieldArray.forEach((field) => {
            if (field === 'itemLength') {
                setValue(`dimensions.${index}.length`, data[field]);
            } else {
                setValue(`dimensions.${index}.${field}`, data[field]);
            }
        });
        handleVisualizeUnitClose();
    };

    return (
        <div className="!pt-0 !mt-0">
            <div className="flex flex-col gap-y-4">
                {dimensionsOptionFields.map((item: any, index) => (
                    <div className={`flex xl:flex-row flex-col ${shouldAnimateRef.current ? 'animate-field-item' : ''}`} key={item.uuid}>
                        <div className={`stepper-content-container `}>
                            <div className="rounded-lg overflow-hidden border border-utilityGray200">
                                <div className=" bg-utilityGray300 flex items-center gap-x-2 p-3 border-b border-utilityGray200">
                                    <label className="text-grayLight900 text-xs font-medium flex-1">Line Item {index + 1}</label>
                                    <Controller
                                        name={`dimensions.${index}.isStack`}
                                        control={control}
                                        render={({ field: { value, onChange } }) => {
                                            const blocked = isStackingBlocked(primaryEquipmentType, dimensions?.[index]?.height, weightMeasurement);
                                            const blockedMessage = getStackingBlockedMessage(primaryEquipmentType, dimensions?.[index]?.height, weightMeasurement);

                                            const toggleEl = (
                                                <Toggle
                                                    size="xs"
                                                    isChecked={blocked || value}
                                                    onChange={onChange}
                                                    isDisabled={blocked}
                                                    labelClassName={`flex items-center`}
                                                    switchClassName={blocked ? 'cursor-not-allowed' : 'cursor-pointer'}
                                                    className={blocked ? 'pointer-events-none' : ''}
                                                    name="isStack"
                                                    label={'Do Not Stack'}
                                                    labelPosition="left"
                                                    labelTextClassName={`!text-xs text-nowrap`}
                                                    id={`doNotStack-${index}`}
                                                />
                                            );

                                            return blocked ? (
                                                <TooltipCmp message={blockedMessage} childrenClassName="!flex">
                                                    <div className="inline-block">{toggleEl}</div>
                                                </TooltipCmp>
                                            ) : (
                                                toggleEl
                                            );
                                        }}
                                    />
                                    {!isHiddenFieldInBooking && (
                                        <>
                                            <TooltipCmp message={t('Common.Duplicate')} childrenClassName="!flex">
                                                <ButtonCmp className="btn-link-secondary" size="none" onClick={handleDuplicate(index)}>
                                                    <Copy04 className="w-4 h-4" />
                                                </ButtonCmp>
                                            </TooltipCmp>
                                        </>
                                    )}
                                    <TooltipCmp message={t('Common.Visualize_Unit')} childrenClassName="!flex">
                                        <ButtonCmp className="btn-link-secondary" size="none" onClick={handleVisualizeUnit(index)}>
                                            <Container className="w-4 h-4" />
                                        </ButtonCmp>
                                    </TooltipCmp>
                                    {!isHiddenFieldInBooking && (
                                        <>
                                            <TooltipCmp message={t('Common.Delete')} childrenClassName="!flex">
                                                <ButtonCmp className="btn-link-secondary" size="none" onClick={handleRemove(index)}>
                                                    <Trash01 className="w-4 h-4" />
                                                </ButtonCmp>
                                            </TooltipCmp>
                                        </>
                                    )}
                                </div>
                                <div className="w-full stepper-lable-content-wrap p-3 flex flex-wrap gap-y-3 gap-x-3 [&>*]:w-[calc(20%_-_10px)]">
                                    <Controller
                                        name={`dimensions.${index}.handlingUnit`}
                                        control={control}
                                        render={({ field: { value, onChange, name }, fieldState: { error } }) => (
                                            <SelectBox
                                                label={t('Common.Handling_unit')}
                                                labelClassName="form_label block mb-1.5"
                                                name="handlingUnit"
                                                id="handlingUnit"
                                                className={`form_control`}
                                                placeholder={t('Common.Handling_unit')}
                                                options={handlingUnitOptions}
                                                onChangeFunc={handleOption(onChange, name)}
                                                isError={!!error?.message}
                                                value={handlingUnitOptions.find((handlingUnitOption: any) => handlingUnitOption.value === parseInt(value) || '')}
                                                isDisabled={isHiddenFieldInBooking}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`dimensions.${index}.handlingUnitNo`}
                                        control={control}
                                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                                            <InputText
                                                label={t('Orders.NE_of_HU')}
                                                labelClassName="form_label block mb-1.5"
                                                inputName="handlingUnitNo"
                                                inputType="number"
                                                maxLength={3}
                                                isLoading={false}
                                                isAllowDecimal={false}
                                                onChangeFunc={handleInputChange(index)}
                                                onBlur={(e: any) => {
                                                    if (!e.target.value) {
                                                        onChange(0);
                                                    }
                                                }}
                                                isError={!!error?.message}
                                                shouldFormatNumber={true}
                                                placeholder={t('Orders.NE_of_HU')}
                                                value={value ?? ''}
                                                disabled={isHiddenFieldInBooking}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`dimensions.${index}.weight`}
                                        control={control}
                                        render={({ field: { value }, fieldState: { error } }) => (
                                            <InputText
                                                label={
                                                    <>
                                                        {t('QuotingHub.QuotingDashboard.Unit_Weight')}
                                                        &nbsp;(
                                                        {weightMeasurement?.toLowerCase()})
                                                    </>
                                                }
                                                labelClassName="form_label block mb-1.5"
                                                inputName="weight"
                                                inputType="number"
                                                placeholder={`Unit Weight (${weightMeasurement?.toLowerCase()})`}
                                                value={value ?? ''}
                                                onChangeFunc={handleInputChange(index)}
                                                onBlur={checkFreightClass(index)}
                                                required
                                                isAllowDecimal={true}
                                                maxLength={7}
                                                shouldFormatNumber={true}
                                                inputValueUnitType={weightMeasurement?.toLowerCase()}
                                                errorText={error?.type === 'max' ? error?.message : ''}
                                                isIconError={!!error?.message}
                                                className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                disabled={isHiddenFieldInBooking}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`dimensions.${index}.totalWeight`}
                                        control={control}
                                        render={({ field: { value }, fieldState: { error } }) => (
                                            <InputText
                                                label={
                                                    <>
                                                        {t('Common.Total_Weight')}
                                                        &nbsp;(
                                                        {weightMeasurement?.toLowerCase()})
                                                    </>
                                                }
                                                labelClassName="form_label block mb-1.5"
                                                inputName="totalWeight"
                                                inputType="number"
                                                placeholder={`Total Weight (${weightMeasurement?.toLowerCase()})`}
                                                value={value ?? ''}
                                                onChangeFunc={handleInputChange(index)}
                                                onBlur={checkFreightClass(index)}
                                                required
                                                errorText={error?.type === 'max' ? error?.message : ''}
                                                isIconError={!!error?.message}
                                                isAllowDecimal={true}
                                                maxLength={7}
                                                shouldFormatNumber={true}
                                                inputValueUnitType={weightMeasurement?.toLowerCase()}
                                                className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                disabled={isHiddenFieldInBooking}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`dimensions.${index}.freightClass`}
                                        control={control}
                                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                                            <InputText
                                                label={t('QuotingHub.Common.Freight_Class')}
                                                labelClassName="form_label block mb-1.5"
                                                isLoading={false}
                                                inputName="freightClass"
                                                inputType="number"
                                                value={value ?? ''}
                                                placeholder="FC"
                                                maxLength={3}
                                                onChangeFunc={handleChangeWithErrorClear(index, 'freightClass')(onChange)}
                                                shouldFormatNumber={true}
                                                className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                disabled={isHiddenFieldInBooking}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`dimensions.${index}.length`}
                                        control={control}
                                        render={({ field: { value }, fieldState: { error } }) => (
                                            <InputText
                                                label={
                                                    <>
                                                        {t('QuotingHub.QuotingDashboard.Length')}
                                                        &nbsp;(
                                                        {lengthMeasurementLabel?.toLowerCase()})
                                                    </>
                                                }
                                                labelClassName="form_label block mb-1.5"
                                                inputName="length"
                                                inputType="number"
                                                placeholder={`L (${lengthMeasurementLabel?.toLowerCase()})`}
                                                value={value ?? ''}
                                                isAllowDecimal={true}
                                                maxLength={4}
                                                shouldFormatNumber={true}
                                                onChangeFunc={handleInputChange(index)}
                                                onBlur={checkFreightClass(index)}
                                                inputValueUnitType={lengthMeasurementLabel?.toLowerCase()}
                                                required
                                                errorText={error?.type === 'max' ? error?.message : ''}
                                                isIconError={!!error?.message}
                                                className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                disabled={isHiddenFieldInBooking}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`dimensions.${index}.width`}
                                        control={control}
                                        render={({ field: { value }, fieldState: { error } }) => (
                                            <InputText
                                                label={
                                                    <>
                                                        {t('QuotingHub.QuotingDashboard.Width')}
                                                        &nbsp;(
                                                        {lengthMeasurementLabel?.toLowerCase()})
                                                    </>
                                                }
                                                labelClassName="form_label block mb-1.5"
                                                inputName="width"
                                                inputType="number"
                                                placeholder={`W (${lengthMeasurementLabel?.toLowerCase()})`}
                                                value={value ?? ''}
                                                onChangeFunc={handleInputChange(index)}
                                                onBlur={checkFreightClass(index)}
                                                required
                                                isAllowDecimal={true}
                                                maxLength={4}
                                                shouldFormatNumber={true}
                                                inputValueUnitType={lengthMeasurementLabel?.toLowerCase()}
                                                errorText={error?.type === 'max' ? error?.message : ''}
                                                isIconError={!!error?.message}
                                                className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                disabled={isHiddenFieldInBooking}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`dimensions.${index}.height`}
                                        control={control}
                                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                                            <InputText
                                                label={
                                                    <>
                                                        {t('QuotingHub.QuotingDashboard.Height')}
                                                        &nbsp;(
                                                        {lengthMeasurementLabel?.toLowerCase()})
                                                    </>
                                                }
                                                labelClassName="form_label block mb-1.5"
                                                isLoading={false}
                                                inputType="number"
                                                inputName="height"
                                                placeholder={`H (${lengthMeasurementLabel?.toLowerCase()})`}
                                                isAllowDecimal={true}
                                                maxLength={4}
                                                shouldFormatNumber={true}
                                                inputValueUnitType={lengthMeasurementLabel?.toLowerCase()}
                                                value={value ?? ''}
                                                onChangeFunc={handleChangeWithErrorClear(index, 'height')(onChange)}
                                                onBlur={checkFreightClass(index)}
                                                required
                                                errorText={error?.type === 'max' ? error?.message : ''}
                                                isIconError={!!error?.message}
                                                className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                disabled={isHiddenFieldInBooking}
                                            />
                                        )}
                                    />

                                    <Controller
                                        name={`dimensions.${index}.commodity`}
                                        control={control}
                                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                                            <SelectBox
                                                parentClassName="!w-[calc(40%_-_6.4px)]"
                                                placeholder={t('Common.Select_commodity')}
                                                autoCompleteSelect={true}
                                                name="commodity"
                                                id={`commodity-${index}`}
                                                className="form_control"
                                                label={t('Common.Commodity')}
                                                labelClassName="form_label block mb-1.5"
                                                isSearchable={true}
                                                options={commodityOptions || []}
                                                value={
                                                    value
                                                        ? commodityOptions?.find((e: any) => e?.value == value) || {
                                                              value: value,
                                                              label: value,
                                                          }
                                                        : null
                                                }
                                                onChangeFunc={handleChangeWithErrorClear(index, 'commodity')(onChange)}
                                                isClearable={true}
                                                onCreateOption={(e: any) => {
                                                    onChange(e);
                                                }}
                                                errorText={error?.message ? true : false}
                                                isShowErrorMsg={false}
                                            />
                                        )}
                                    />
                                </div>

                                {/* Client decision — read-only routing display, same gate (stopCount > 2) and
                                    layout convention (own bordered row below the field grid) as the Add Quote
                                    wizard's editable version. Always shown when the quote has middle stops —
                                    there's no "address not yet picked" case at booking time, unlike creation. */}
                                {stopCount > 2 && (
                                    <div className="border-t border-utilityGray200 mx-3 pb-3 pt-3 flex gap-x-3">
                                        <SelectBox
                                            parentClassName="flex-1"
                                            label={t('MyQuote.Picks_Up_At')}
                                            labelClassName="form_label block mb-1.5"
                                            name="pickupStopPositionDisplay"
                                            id={`pickupStopPositionDisplay-${index}`}
                                            className="form_control"
                                            isDisabled
                                            options={pickupOptions}
                                            value={pickupOptions.find((option) => option.value === (dimensions?.[index]?.pickupStopPosition ?? 1)) ?? pickupOptions[0]}
                                            onChangeFunc={() => {}}
                                        />
                                        <SelectBox
                                            parentClassName="flex-1"
                                            label={t('MyQuote.Delivers_To')}
                                            labelClassName="form_label block mb-1.5"
                                            name="dropStopPositionDisplay"
                                            id={`dropStopPositionDisplay-${index}`}
                                            className="form_control"
                                            isDisabled
                                            options={dropOptions}
                                            value={dropOptions.find((option) => option.value === (dimensions?.[index]?.dropStopPosition ?? stopCount)) ?? dropOptions[dropOptions.length - 1]}
                                            onChangeFunc={() => {}}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {!isHiddenFieldInBooking && (
                <div className="flex gap-x-3 w-full justify-end items-center stepper-content-container field-without-top-border animate-field-item">
                    <ButtonCmp className="btn-outline-primary" icon={<Plus className="w-4 h-4" />} onClick={handleAdd}>
                        {t('Common.Add_Line_Item')}
                    </ButtonCmp>
                </div>
            )}
            {currentData && (
                <VisualizeUnit
                    currentData={currentData}
                    handleClose={handleVisualizeUnitClose}
                    weightMeasurement={weightMeasurement}
                    lengthMeasurement={lengthMeasurement}
                    isEnable={false}
                    parentFreightClass={getValues(`dimensions.${currentData.index}.freightClass`)}
                    unitArr={handlingUnitOptions}
                    isFormDataHaveKey={true}
                    handleVisualizeUnitSubmit={handleVisualizeUnitSubmit}
                />
            )}
        </div>
    );
};

export default memo(LineItem);
